class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupVariantDropdowns();

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.fetchRecommendations();
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.cart-drawer__card-variant')) {
        this.querySelectorAll('.cart-drawer__card-variant-dropdown:not([hidden])').forEach((dd) => {
          dd.setAttribute('hidden', '');
          dd.previousElementSibling.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble') || document.querySelector('.header-menu__button');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-label', cartLink.getAttribute('aria-label') || 'Cart');
    cartLink.setAttribute('tabindex', '0');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const header = document.querySelector('header');
    if (header) {
      const headerBottom = header.getBoundingClientRect().bottom;
      this.style.setProperty('--drawer-top', `${headerBottom}px`);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    this.fetchRecommendations();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble' });
    }
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  fetchRecommendations() {
    const recContainer = this.querySelector('.cart-drawer__recommendations');
    if (!recContainer) return;

    const productId = recContainer.dataset.productId;
    if (!productId) return;

    const productsContainer = recContainer.querySelector('.cart-drawer__recommendations-products');
    if (!productsContainer) return;

    const baseUrl = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=2`;

    fetch(`${baseUrl}&intent=complementary`)
      .then((response) => response.json())
      .then((data) => {
        if (data.products && data.products.length > 0) {
          return data.products;
        }
        return fetch(`${baseUrl}&intent=related`)
          .then((r) => r.json())
          .then((d) => (d.products && d.products.length > 0 ? d.products : []));
      })
      .then((products) => {
        if (products.length > 0) {
          const hasVisible = this.renderRecommendations(productsContainer, products);
          recContainer.style.display = hasVisible ? '' : 'none';
        } else {
          recContainer.style.display = 'none';
        }
      })
      .catch((err) => {
        console.error('Failed to fetch recommendations:', err);
        recContainer.style.display = 'none';
      });

    this.setupRecommendationsToggle(recContainer);
  }

  renderRecommendations(container, products) {
    const cartItemIds = Array.from(document.querySelectorAll('[data-quantity-variant-id]')).map(
      (el) => el.dataset.quantityVariantId
    );

    const filtered = products.filter((product) => {
      const variant = product.variants && product.variants[0];
      return variant && !cartItemIds.includes(String(variant.id));
    });

    if (filtered.length === 0) {
      container.innerHTML = '';
      return false;
    }

    container.innerHTML = filtered
      .map((product) => {
        const variant = product.variants[0];
        const image = product.featured_image
          ? product.featured_image
          : product.images && product.images[0]
          ? product.images[0]
          : null;
        const imageSrc = image ? (typeof image === 'string' ? image : image.src || image.url || '') : '';
        const imageAlt = image && typeof image === 'object' ? image.alt || product.title : product.title;

        const price = Shopify.formatMoney
          ? Shopify.formatMoney(variant.price)
          : (variant.price / 100).toFixed(2) + '€';

        return `<div class="cart-drawer__recommendations-product" data-variant-id="${variant.id}" data-product-url="${product.url}">
            <div class="cart-drawer__recommendations-product-image">
              ${imageSrc ? `<img src="${imageSrc}" alt="${imageAlt}" loading="lazy" width="60" height="80">` : ''}
              <button type="button" class="cart-drawer__recommendations-product-add" aria-label="Add ${product.title} to cart" data-variant-id="${variant.id}">
                <span class="svg-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 10">
                    <path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/>
                  </svg>
                </span>
              </button>
            </div>
            <div class="cart-drawer__recommendations-product-info">
              <a href="${product.url}" class="cart-drawer__recommendations-product-title">${product.title}</a>
              <span class="cart-drawer__recommendations-product-price">${price}</span>
            </div>
          </div>`;
      })
      .join('');

    container.querySelectorAll('.cart-drawer__recommendations-product-add').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addRecommendationToCart(btn.dataset.variantId);
      });
    });

    return true;
  }

  addRecommendationToCart(variantId) {
    const body = JSON.stringify({
      items: [{ id: parseInt(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        this.renderContents(parsedState);
      })
      .catch((err) => {
        console.error('Failed to add recommendation to cart:', err);
      });
  }

  setupRecommendationsToggle(recContainer) {
    const toggle = recContainer.querySelector('.cart-drawer__recommendations-toggle');
    const products = recContainer.querySelector('.cart-drawer__recommendations-products');
    if (!toggle || !products) return;

    if (toggle.dataset.bound) return;
    toggle.dataset.bound = 'true';

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isExpanded);
      if (isExpanded) {
        products.setAttribute('hidden', '');
      } else {
        products.removeAttribute('hidden');
      }
    });
  }

  setupVariantDropdowns() {
    this.addEventListener('click', (e) => {
      const toggle = e.target.closest('.cart-drawer__card-variant-toggle');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        const dropdown = toggle.nextElementSibling;
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        this.querySelectorAll('.cart-drawer__card-variant-toggle[aria-expanded="true"]').forEach((other) => {
          if (other !== toggle) {
            other.setAttribute('aria-expanded', 'false');
            other.nextElementSibling.setAttribute('hidden', '');
          }
        });

        toggle.setAttribute('aria-expanded', !isOpen);
        if (isOpen) {
          dropdown.setAttribute('hidden', '');
        } else {
          dropdown.removeAttribute('hidden');
        }
        return;
      }

      const option = e.target.closest('.cart-drawer__card-variant-option');
      if (option) {
        e.preventDefault();
        e.stopPropagation();
        this.switchVariant(option);
      }
    });
  }

  switchVariant(optionBtn) {
    const newVariantId = parseInt(optionBtn.dataset.variantId);
    const currentVariantId = parseInt(optionBtn.dataset.currentVariantId);
    const lineIndex = parseInt(optionBtn.dataset.lineIndex);

    if (newVariantId === currentVariantId) {
      const dropdown = optionBtn.closest('.cart-drawer__card-variant-dropdown');
      dropdown.setAttribute('hidden', '');
      dropdown.previousElementSibling.setAttribute('aria-expanded', 'false');
      return;
    }

    const qtyInput = this.querySelector(`#Drawer-quantity-${lineIndex}`);
    const quantity = qtyInput ? parseInt(qtyInput.value) : 1;

    const updates = {};
    updates[currentVariantId] = 0;
    updates[newVariantId] = quantity;

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    const card = optionBtn.closest('.cart-drawer__card');
    const overlay = card && card.querySelector('.cart-drawer__card-loading-overlay');
    if (card) card.classList.add('cart-drawer__card--loading');
    if (overlay) overlay.classList.remove('hidden');

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          console.error('Variant switch error:', parsedState.errors);
          if (card) card.classList.remove('cart-drawer__card--loading');
          if (overlay) overlay.classList.add('hidden');
          return;
        }
        this.renderContents(parsedState);
      })
      .catch((err) => {
        console.error('Failed to switch variant:', err);
        if (card) card.classList.remove('cart-drawer__card--loading');
        if (overlay) overlay.classList.add('hidden');
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      });
    }
    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
